import DATA from "./designations.json";
import { lang, t } from "./i18n";

/** Désignations d'unités en anglais OTAN (décision d'Alexis du 24/10/2026 : « 27e BIM » → « 27 Mtn Inf Bde »,
 *  « 1er RCP » → « 1 Para Inf Regt », « 3e DIV » → « 3 (FR) Div », abréviations APP-6/AAP-15, jamais de code
 *  interne affiché, toponymes inchangés). Jumeau de `server/generex_engine/designations.py` : mêmes règles, mêmes
 *  tables (`designations.json`, copie exacte de `server/generex_engine/data/langue/designations.json`, comparée par
 *  un essai), mêmes vecteurs d'essai (`designations_essais.json` du serveur).
 *
 *  Le nom français reste la clé partout (codes, unitName, références) : `unitDesignation` ne sert qu'à l'affichage,
 *  quand l'interface est en anglais. */

type Data = {
  ordinaux: string[];
  sigles: Record<string, string>;
  nation_par_defaut: Record<string, string>;
  sans_numero: Record<string, string>;
  elementaires: Record<string, string>;
  libelles: Record<string, string>;
  parentheses: Record<string, string>;
  groupements: Record<string, string>;
  motifs: [string, string][];
  cas_particuliers: Record<string, string>;
  inchanges: string[];
};

const D = DATA as unknown as Data;

const W = "[\\p{L}\\p{N}_]"; // « \w » unicode, comme en Python
const NAT = "\\(([A-Z]{2})\\)";
const BEFORE = "(?<![\\p{L}\\p{N}_/])";
const AFTER = "(?![A-Za-z0-9])";
const THE = `(?:(?<!${W})([Tt]he)\\s+)?`;
const TEAM_WORDS = "(?:S/GTIA|SGTIA|GTIA)";
const ENGLISH_ELEMENTS = ["company", "squadron", "battery", "flight", "platoon", "troop", "battalion"];
const PLURALS: Record<string, string> = {
  Companies: "Coys",
  Squadrons: "Sqns",
  Batteries: "Btys",
  Platoons: "Pls",
  Troops: "Tps",
  Battalions: "Bns",
};

const escapeRe = (s: string) => s.replace(/[.*+?^${}()|[\]\\/]/g, "\\$&");
const alt = (words: Iterable<string>) => [...words].sort((a, b) => b.length - a.length).map(escapeRe).join("|");
const re = (source: string, flags = "") => new RegExp(source, `u${flags}`);
const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

function buildRules() {
  const ordinal = `(?:${alt(D.ordinaux)})`;
  const sigles = alt(Object.keys(D.sigles));
  const sans = alt(Object.keys(D.sans_numero));
  const elems = Object.keys(D.elementaires);
  const elemFr = alt(elems.filter((w) => !ENGLISH_ELEMENTS.includes(w)));
  const elemEn = alt(elems.filter((w) => ENGLISH_ELEMENTS.includes(w)));
  const named = Object.keys(D.groupements).filter((g) => !["GTIA", "SGTIA", "S/GTIA"].includes(g));
  const end = `(?!${W})`; // « \b » final de Python
  const motif = (p: string) => p.replace(/^\^/, "").replace(/\$$/, "");
  const ordEn = "\\d+(?:st|nd|rd|th)";
  return {
    numbered: re(`^(\\d{1,4})${ordinal}?\\s?(?:${NAT}\\s?)?(${sigles})(?:\\s?${NAT})?$`),
    numberedText: re(`${THE}${BEFORE}(\\d{1,4})(?:${ordinal}\\s?)?(?:${NAT})?(${sigles})${AFTER}`, "g"),
    teamNumberedText: re(
      `${THE}${BEFORE}(${TEAM_WORDS})[ _]+(\\d{1,4})(?:${ordinal}\\s?)?(?:${NAT})?(${sigles})${AFTER}`,
      "g",
    ),
    plain: re(`^(${sans})(?:\\s?${NAT})?$`),
    plainText: re(`${BEFORE}(${sans})(?:${NAT})?${AFTER}`, "g"),
    elemFr: re(`^(\\d+)\\s*${ordinal}\\.?\\s+(${elemFr})${end}\\.?(.*)$`, "i"),
    elemEn: re(`^(\\d+)(?:st|nd|rd|th)\\s+(${elemEn})${end}(.*)$`, "i"),
    elemFrText: re(`${THE}${BEFORE}(\\d+)\\s*${ordinal}\\.?\\s+(${elemFr})${end}\\.?`, "gi"),
    elemEnText: re(`${THE}${BEFORE}(\\d+)(?:st|nd|rd|th)\\s+(${alt(ENGLISH_ELEMENTS.map(capitalize))})${end}`, "g"),
    wordNumber: re(`^(${elemFr}|${elemEn})\\s+(\\d+)$`, "i"),
    team: re(`^(${TEAM_WORDS})[\\s_-]+(.+)$`),
    teamText: re(`${THE}${BEFORE}(${TEAM_WORDS})(?:[ _]+([A-Z0-9])(?![\\p{L}\\p{N}_(]))?(?![\\p{L}\\p{N}_/])`, "g"),
    namedText: re(`${BEFORE}(${alt(named)})${AFTER}`, "g"),
    libelles: re(`^(${alt(Object.keys(D.libelles))})${end}(.*)$`, "i"),
    libellesText: re(
      `${BEFORE}(${alt(Object.keys(D.libelles))})(?![\\p{L}\\p{N}_-])((?:\\s+\\d+)?(?:\\s*\\([^)\\n]*\\))?)`,
      "gi",
    ),
    motifs: D.motifs.map(([p, r]) => [re(p), r] as const),
    motifsText: D.motifs.map(([p, r]) => [re(`${BEFORE}${motif(p)}(?!${W})`, "g"), r] as const),
    particularText: re(`${BEFORE}(${alt(Object.keys(D.cas_particuliers))})(?!${W})`, "g"),
    afterSlashText: re(`(?<=[A-Za-z])/(\\d{1,4})(?:${ordinal}\\s?)?(?:${NAT})?(${sigles})${AFTER}`, "g"),
    pluralText: re(
      `(?:(?<!${W})([Tt]he)\\s+)?(?<!${W})(${ordEn}(?:, ${ordEn})* and ${ordEn}) (${Object.keys(PLURALS).join("|")})${end}`,
      "g",
    ),
  };
}

let rules: ReturnType<typeof buildRules> | null = null;
const R = () => (rules ??= buildRules());

type Groups = (string | undefined)[];

/** `text.replace` dont le rappel reçoit les seuls groupes (indéfinis compris), comme `m.groups()` en Python. */
function sub(pattern: RegExp, text: string, fn: (groups: Groups, match: string) => string): string {
  return text.replace(pattern, (match: string, ...rest: unknown[]) => {
    const groups = rest.slice(0, -2) as Groups; // ni la position ni la chaîne
    return fn(groups, match);
  });
}

function fill(template: string, groups: Groups): string {
  let out = template;
  groups.forEach((g, i) => {
    out = out.split(`{${i + 1}}`).join(g ?? "");
  });
  return out.replace(/\s+/g, " ").trim();
}

function nation(code: string | undefined, sigle: string): string {
  const value = code || D.nation_par_defaut[sigle];
  return value ? `(${value}) ` : "";
}

function numbered(number: string, nat1: string | undefined, sigle: string, nat2?: string): string {
  return `${parseInt(number, 10)} ${nation(nat1 || nat2, sigle)}${at(D.sigles, sigle)}`;
}

function tail(text: string): string {
  const table = D.parentheses;
  const out = text.replace(/\([^)]*\)/g, (group) =>
    group in table ? at(table, group) : group.replace(/(?<![\p{L}\p{N}_])GPE(?![\p{L}\p{N}_])/gu, table.GPE ?? "GPE"),
  );
  return out.replace(/\s+/g, " ").trim();
}

/** Position de la barre qui sépare une unité de sa formation, hors parenthèses (« (3 GPE/GBC) ») ; -1. */
function slash(text: string): number {
  let depth = 0;
  for (let i = 0; i < text.length; i++) {
    const char = text.charAt(i);
    depth += (char === "(" ? 1 : 0) - (char === ")" ? 1 : 0);
    if (char === "/" && depth === 0) return i;
  }
  return -1;
}

function join(head: string, rest: string): string {
  // Unité suivie de sa formation (« 1re Cie/1erRCP » → « 1 Coy/1 Para Inf Regt », « SGTIA C (fictif)/GTIA
  // interarmes » → « Coy Team C (notional)/Combined Arms BG »).
  const cut = slash(rest);
  if (cut >= 0 && cut < rest.length - 1) return `${join(head, rest.slice(0, cut))}/${englishDesignation(rest.slice(cut + 1))}`;
  const t = tail(rest);
  return t ? `${head} ${t}`.trim() : head;
}

const isDigit = (s: string) => /^\d/.test(s);
/** Valeur d'une table (clé connue, trouvée par l'expression qui l'énumère). */
const at = (table: Record<string, string>, key: string | undefined): string => table[key ?? ""] ?? key ?? "";

/** Désignation d'une unité en anglais OTAN ; inconnue, elle revient telle quelle (sans espaces autour). */
export function englishDesignation(name: string | null | undefined): string {
  const text = (name ?? "").trim();
  if (!text) return "";
  const r = R();
  if (text in D.cas_particuliers) return at(D.cas_particuliers, text);
  if (D.inchanges.includes(text)) return text;
  if (text in D.groupements) return at(D.groupements, text);
  const team = r.team.exec(text);
  if (team) {
    const kind = team[1] === "GTIA" ? "BG" : "Coy Team";
    const rest = (team[2] ?? "").trim();
    if (/^[A-Z0-9](?![\p{L}\p{N}_])/u.test(rest)) return join(`${kind} ${rest.charAt(0)}`, rest.slice(1));
    const inner = englishDesignation(rest);
    if (inner !== rest && isDigit(inner)) return `${inner} ${kind}`;
    return `${kind} ${inner}`;
  }
  for (const [pattern, template] of r.motifs) {
    const m = pattern.exec(text);
    if (m) return englishDesignations(fill(template, m.slice(1)));
  }
  let m = r.numbered.exec(text);
  if (m) return numbered(m[1] ?? "", m[2], m[3] ?? "", m[4]);
  m = r.plain.exec(text);
  if (m) return `${nation(m[2], m[1] ?? "")}${at(D.sans_numero, m[1])}`;
  m = r.elemFr.exec(text) ?? r.elemEn.exec(text);
  if (m) return join(`${parseInt(m[1] ?? "", 10)} ${at(D.elementaires, m[2]?.toLowerCase())}`, m[3] ?? "");
  m = r.wordNumber.exec(text);
  if (m) return `${at(D.elementaires, m[1]?.toLowerCase())} ${parseInt(m[2] ?? "", 10)}`;
  m = r.libelles.exec(text);
  if (m) return join(at(D.libelles, m[1]?.toLowerCase()), m[2] ?? "");
  return englishDesignations(text);
}

function dropThe(article: string | undefined, english: string): string {
  return article && !isDigit(english) ? `${article} ${english}` : english;
}

/** Toutes les désignations reconnues d'un texte anglais en anglais OTAN (« the 1(TI)DIVBLIND » → « 1 (TI) Armd
 *  Div », « GTIA 1erRCP » → « 1 Para Inf Regt BG ») ; toponymes et mots ordinaires inchangés. */
export function englishDesignations(text: string): string {
  if (!text) return text;
  const r = R();
  let out = sub(r.particularText, text, (g) => at(D.cas_particuliers, g[0]));
  for (const [pattern, template] of r.motifsText) out = sub(pattern, out, (g) => fill(template, g));
  out = sub(r.namedText, out, (g) => at(D.groupements, g[0]));
  out = sub(r.teamNumberedText, out, (g) => `${numbered(g[2]!, g[3], g[4]!)} ${g[1] === "GTIA" ? "BG" : "Coy Team"}`);
  out = sub(r.numberedText, out, (g) => dropThe(g[0], numbered(g[1]!, g[2], g[3]!)));
  out = sub(r.teamText, out, (g) => {
    const kind = g[1] === "GTIA" ? "BG" : "Coy Team";
    const english = g[2] ? `${kind} ${g[2]}` : kind;
    return g[0] ? `${g[0]} ${english}` : english;
  });
  out = out.replace(/(?<![\p{L}\p{N}_])Company [Tt]eam(?= [A-Z0-9])/gu, "Coy Team");
  out = sub(r.plainText, out, (g) => `${nation(g[1], g[0]!)}${at(D.sans_numero, g[0])}`);
  out = sub(r.pluralText, out, (g) => `${g[1]!.replace(/(?<=\d)(?:st|nd|rd|th)/g, "")} ${at(PLURALS, g[2])}`);
  const elementary = (g: Groups) => dropThe(g[0], `${parseInt(g[1]!, 10)} ${at(D.elementaires, g[2]?.toLowerCase())}`);
  out = sub(r.elemFrText, out, elementary);
  out = sub(r.elemEnText, out, elementary);
  // Formation après une barre (« 1 Coy/1erRCP » → « 1 Coy/1 Para Inf Regt ») ; « 1/1 », « 2/3 » intacts.
  out = sub(r.afterSlashText, out, (g) => `/${numbered(g[0]!, g[1], g[2]!)}`);
  return sub(r.libellesText, out, (g) => join(at(D.libelles, g[0]?.toLowerCase()), g[1] ?? ""));
}

/** Nom d'unité à l'écran : désignation OTAN quand l'interface est en anglais, le nom tel quel en français. */
export function unitDesignation(name: string | null | undefined): string {
  if (name == null) return "";
  // Second passage sur le texte : libellé composé dont la suite cite une autre unité (« 3ème Compagnie · 11BP »).
  return lang() === "en" ? englishDesignations(englishDesignation(name)) : name;
}

/** Texte d'interface qui cite des unités (incident, mission, journal) : désignations OTAN en anglais. */
export function unitDesignations(text: string | null | undefined): string {
  if (text == null) return "";
  return lang() === "en" ? englishDesignations(text) : text;
}

/** Unité citée par un paragraphe de l'ordre, ou son repli « éléments de la 10(EN)BRIGBLIND » (échelon vidé) : en
 *  anglais, « elements of 10 (EN) Armd Bde ». */
export function citedUnit(name: string | null | undefined): string {
  if (name == null) return "";
  if (lang() !== "en") return name;
  const m = /^éléments (?:de la |de l'|de l’|du |des |de )(.+)$/u.exec(name.trim());
  return m ? t("éléments de {unit}", { unit: englishDesignation(m[1]) }) : englishDesignation(name);
}
